<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { Plus } from 'lucide-vue-next';
// shadcn-vue 컴포넌트 임포트
import { Button } from '@/components/shadcnui/button';
import { Input } from '@/components/shadcnui/input';
import { Label } from '@/components/shadcnui/label';
import { Skeleton } from '@/components/shadcnui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/shadcnui/tooltip';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/shadcnui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/shadcnui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/shadcnui/dialog';
import { Pagination, PaginationContent, PaginationEllipsis, PaginationItem, PaginationNext, PaginationPrevious } from '@/components/shadcnui/pagination';
// composable 및 store 임포트
import { useChipset } from '@/composable/useChipset';
import { usePkgtype } from '@/composable/usePkgtype';
import { usePkg } from '@/composable/usePkg';
import { useRSStore } from '@/store/useRSStore';
import { useUserStore } from '@/store/useUserStore';
// 타입 임포트
import type { pkg, addpkg } from '../types/types';

// 스토어 및 composable 초기화
const RSStore = useRSStore();
const UserStore = useUserStore();
const { onReadChipsetList } = useChipset();
const { onReadPkgtypeList } = usePkgtype();
const { onReadPkgList, onAddPkg, onUpdatePkg, onDeletePkg, onPaginationPkg } = usePkg();

// 상태 관리
const isDialogOpen = ref(false);
const isUpdateMode = ref(false);
const currentPage = ref(1);
const filterChipset = ref(0);
const filterPackageType = ref(0);
const totalpage = ref(1);
const isLoading = ref(true);
const settingMode = ref(1);
const formData = ref<pkg>({ id: 0, chipset_id: 0, pkgtype_id: 0, pkgname: '', appname: '' });
const isFormInvalid = computed(() => {
  return !formData.value.pkgname?.trim() || !formData.value.appname?.trim() || !formData.value.chipset_id || !formData.value.pkgtype_id;
});

// 필터링 조건 변경 시 모델 리스트 업데이트
watch([filterChipset, filterPackageType], async () => {
  if (!settingMode.value) {
    UserStore.pkgfilter.chipset_id = filterChipset.value;
    UserStore.pkgfilter.pkgtype_id = filterPackageType.value;
    console.log("필터링 조건 업데이트됨:", UserStore.pkgfilter);

  currentPage.value = 1;
  UserStore.pkgfilter.page = 1;
  totalpage.value = await onPaginationPkg(UserStore.pkgfilter) ?? 1;
  await onReadPkgList(UserStore.pkgfilter);
  }
});

// 폼 데이터 초기화 함수
const formDataReset = () => {
  formData.value = { id: 0, chipset_id: 0, pkgtype_id: 0, pkgname: '', appname: '' };
};

// 버튼 핸들러 
const openAddDialog = () => {
  isUpdateMode.value = false;
  formDataReset();
  isDialogOpen.value = true;
};
const openUpdateDialog = (pkg: pkg) => {
    if (!UserStore.pkgEdit && !UserStore.pkgDelete) {
    return;
  }
  isUpdateMode.value = true;
  formData.value = { ...pkg };
  isDialogOpen.value = true;
};
const cancelDialog = () => {
  isDialogOpen.value = false;
  formDataReset();
};

// ADD/UPDATE API 호출 핸들러 (API 호출 후 데이터 갱신)
const handleAdd = async () => {
  const addpkg: addpkg = {
    chipset_id: formData.value.chipset_id,
    pkgtype_id: formData.value.pkgtype_id,
    pkgname: formData.value.pkgname,
    appname: formData.value.appname,
  };
  currentPage.value = 1; // 페이지네이션을 첫 페이지로 리셋
  UserStore.pkgfilter.page = 1;
  isLoading.value = true;
  await onAddPkg(addpkg);
  await onReadPkgList(UserStore.pkgfilter).finally(() => {
    isLoading.value = false;
  });
  isDialogOpen.value = false;
};
const handleUpdate = async () => {
  isLoading.value = true;
  await onUpdatePkg(formData.value);
  await onReadPkgList(UserStore.pkgfilter).finally(() => {
    isLoading.value = false;
  });
  isDialogOpen.value = false;
};
const handleDelete = async () => {
  isLoading.value = true;
  await onDeletePkg(formData.value.id);
  await onReadPkgList(UserStore.pkgfilter).finally(() => {
    isLoading.value = false;
  });
  isDialogOpen.value = false;
};

// 페이지네이션 핸들러
const paginationHandler = async (page: number) => {
  if (page < 1) {
    page = 1;
  } else if (page > totalpage.value) {
    page = totalpage.value;
  }
  currentPage.value = page;
  UserStore.pkgfilter.page = page;
  isLoading.value = true;
  await onReadPkgList(UserStore.pkgfilter).finally(() => {
    isLoading.value = false;
  });
};

// 컴포넌트 마운트 시 데이터 로드
onMounted(async () => {
  totalpage.value = await onPaginationPkg(UserStore.pkgfilter) ?? 1;
  if (UserStore.pkgfilter.chipset_id || UserStore.pkgfilter.pkgtype_id) {
    filterChipset.value = UserStore.pkgfilter.chipset_id;
    filterPackageType.value = UserStore.pkgfilter.pkgtype_id;
  }
  if (UserStore.pkgfilter.page) {
    currentPage.value = UserStore.pkgfilter.page;
  }
  await onReadChipsetList({ page: 0, size: 0 });
  await onReadPkgtypeList({ page: 0, size: 0 });
  await onReadPkgList(UserStore.pkgfilter).finally(() => {
    isLoading.value = false;
  });
  settingMode.value = 0;
});
</script>

<template>
  <div class="p-8">
    <!-- 헤더 -->
    <div class="flex items-center justify-between mb-2">
      <h1 class="text-3xl font-bold text-foreground">패키지</h1>
      <!-- 필터링 -->
      <div class="flex gap-4 mb-2">
        <div class="w-48">
          <Label class="mb-2 block">Chipset</Label>
          <Select v-model="filterChipset">
            <SelectTrigger>
              <SelectValue placeholder="전체" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem :value="0">전체</SelectItem>
              <SelectItem v-for="chipset in RSStore.chipsetList" :key="chipset.id" :value="chipset.id">
                {{ chipset.name }}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div class="w-48">
          <Label class="mb-2 block">Package Type</Label>
          <Select v-model="filterPackageType">
            <SelectTrigger>
              <SelectValue placeholder="전체" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem :value="0">전체</SelectItem>
              <SelectItem v-for="pkgtype in RSStore.pkgtypeList" :key="pkgtype.id" :value="pkgtype.id">
                {{ pkgtype.name }}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div class="flex items-end">
          <Button @click="openAddDialog" :disabled="!UserStore.pkgAdd">
            <Plus class="mr-2 h-4 w-4" /> Add
          </Button>
        </div>
      </div>
    </div>

    <!-- 데이터 테이블 -->
    <div class="border rounded-lg bg-card overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow class="bg-muted/50 hover:bg-muted/50">
            <TableHead class="font-semibold w-[80px] px-5">ID</TableHead>
            <TableHead class="font-semibold w-[400px]">Chipset</TableHead>
            <TableHead class="font-semibold w-[500px]">Package Type</TableHead>
            <TableHead class="font-semibold w-[500px]">Package Name</TableHead>
            <TableHead class="font-semibold w-[500px]">App Name</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <!-- 로딩 상태 Skeleton UI 적용-->
          <template v-if="isLoading">
            <TableRow v-for="i in UserStore.itemPerPage" :key="i">
              <TableCell colspan="5" class="px-3">
                <Skeleton class="h-5" />
              </TableCell>
            </TableRow>
          </template>
          <!-- 일반 상태 -->
          <template v-else>
            <TableRow v-for="pkg in RSStore.pkgList" :key="pkg.id" class="cursor-pointer hover:bg-accent/50"
              @click="openUpdateDialog(pkg)">
              <TableCell class="px-5 w-[200px]">{{ pkg.id }}</TableCell>
              <TableCell>{{RSStore.chipsetList.find(c => c.id === pkg.chipset_id)?.name}}</TableCell>
              <TableCell>{{RSStore.pkgtypeList.find(f => f.id === pkg.pkgtype_id)?.name}}</TableCell>
              <TableCell class="">{{ pkg.pkgname }}</TableCell>
              <TableCell>{{ pkg.appname }}</TableCell>
            </TableRow>
          <TableRow v-for="i in (UserStore.itemPerPage - RSStore.pkgList.length)" :key="'empty-' + i">
            <TableCell colspan="5">&nbsp;</TableCell>
          </TableRow>
          </template>
        </TableBody>
      </Table>
    </div>
    <!-- 페이지네이션 -->
    <div class="mt-4 flex justify-center">
      <Pagination v-model:page="currentPage" :total="totalpage * UserStore.itemPerPage"
        :items-per-page="UserStore.itemPerPage" :sibling-count="UserStore.siblingCount" show-edges>
        <PaginationContent v-slot="{ items }" class="flex items-center gap-1">
          <PaginationPrevious @click="paginationHandler(currentPage - RSStore.nextunit)" />
          <template v-for="(item, index) in items">
            <PaginationItem v-if="item.type === 'page'" :key="index" :value="item.value" as-child>
              <Button class="w-10 h-10 p-0" :variant="item.value === currentPage ? 'default' : 'outline'"
                @click="paginationHandler(item.value)">
                {{ item.value }}
              </Button>
            </PaginationItem>
            <PaginationEllipsis v-else :key="item.type" :index="index" />
          </template>
          <PaginationNext @click="paginationHandler(currentPage + RSStore.nextunit)" />
        </PaginationContent>
      </Pagination>
    </div>
    <!-- 추가/수정 다이얼로그 -->
    <Dialog v-model:open="isDialogOpen">
      <DialogContent class="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{{ isUpdateMode ? "패키지 수정" : "패키지 추가" }}</DialogTitle>
          <DialogDescription>
            {{ isUpdateMode ? "패키지 정보를 수정하세요" : "새로운 패키지 정보를 입력하세요" }}
          </DialogDescription>
        </DialogHeader>
        <div class="grid gap-4 py-4 text-foreground">
          <div class="grid gap-2">
            <Label for="chipset">Chipset</Label>
            <Select v-model="formData.chipset_id">
              <SelectTrigger id="chipset">
                <SelectValue placeholder="칩셋 선택" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem v-for="chipset in RSStore.chipsetList" :key="chipset.id" :value="chipset.id">
                  {{ chipset.name }}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div class="grid gap-2">
            <Label for="packageType">Package Type</Label>
            <Select v-model="formData.pkgtype_id">
              <SelectTrigger id="packageType">
                <SelectValue placeholder="패키지 타입 선택" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem v-for="pkgtype in RSStore.pkgtypeList" :key="pkgtype.id" :value="pkgtype.id">
                  {{ pkgtype.name }}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div class="grid gap-2">
            <Label for="packageName">Package Name</Label>
            <Input id="packageName" v-model="formData.pkgname" placeholder="패키지 이름 입력" maxlength="128" />
          </div>
          <div class="grid gap-2">
            <Label for="appName">App Name</Label>
            <Input id="appName" v-model="formData.appname" placeholder="앱 이름 입력" maxlength="64" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" @click="cancelDialog">Cancel</Button>
          <Button v-if="isUpdateMode" variant="destructive" @click="handleDelete" :disabled="!UserStore.pkgDelete">
            Delete
          </Button>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger as-child>
                <div class="inline-block cursor-not-allowed">
                  <Button @click="isUpdateMode ? handleUpdate() : handleAdd()" :disabled="isFormInvalid || (!UserStore.pkgEdit && isUpdateMode)"
                    :class="{ 'pointer-events-none': isFormInvalid }">
                    {{ isUpdateMode ? "Update" : "Add" }}
                  </Button>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p v-if="isFormInvalid">chipset, package type, package name, app name을 입력해주세요.</p>
                <p v-else>패키지를 저장합니다.</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </div>
</template>